import { type Language, type LocalizedText, zLocalizedText } from '@/shared/api';

/**
 * LocalizedText (COM-09) в формах: RU — `{ru}`, World — `{en, es, source_lang, translation_status}`.
 * Значение поля формы — объект контракта целиком (метаданные перевода нужны для показа);
 * в запрос уходит только текст языков рынка (`localizedTextInput`): `source_lang` и
 * `translation_status` выставляет сервер (INT-SVC FR-INT-SVC-22, BR-INT-SVC-69/70).
 */
export function localizedTextOf(
  value: LocalizedText | null | undefined,
  language: Language,
): string {
  const text = value?.[language];
  return typeof text === 'string' ? text : '';
}

/** Пустое значение для формы создания: ключи языков рынка. */
export function emptyLocalizedText(languages: readonly Language[]): LocalizedText {
  const value: LocalizedText = {};
  for (const language of languages) value[language] = '';
  return value;
}

/** Значение поля формы из произвольного значения (ответ сервера, defaultValues): только объект. */
export function toLocalizedText(value: unknown): LocalizedText | null {
  const parsed = zLocalizedText.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Значение для отправки: обрезанные тексты языков рынка, пустые не отправляются (World — язык
 * без текста заполнит автоперевод); нет ни одного текста — null.
 */
export function localizedTextInput(
  value: LocalizedText | null | undefined,
  languages: readonly Language[],
): LocalizedText | null {
  const result: LocalizedText = {};
  let filled = false;
  for (const language of languages) {
    const text = localizedTextOf(value, language).trim();
    if (text) {
      result[language] = text;
      filled = true;
    }
  }
  return filled ? result : null;
}

export interface LocalizedTextRules {
  languages: readonly Language[];
  /** Обязательное поле: заполнен хотя бы один язык рынка (остальные — автоперевод, D13). */
  required?: boolean;
  /** Ограничение длины для каждого языка. */
  max?: number;
  messages: { required: string; tooLong: string };
}

/**
 * zod-схема поля формы на основе сгенерированной `zLocalizedText`: обязательность и длина;
 * ошибка длины — на конкретном языке (путь `поле.en`), обязательность — на поле целиком.
 */
export function localizedTextSchema({
  languages,
  required = false,
  max,
  messages,
}: LocalizedTextRules) {
  return zLocalizedText.nullable().superRefine((value, context) => {
    const filled = languages.some((language) => localizedTextOf(value, language).trim() !== '');
    if (required && !filled) {
      context.addIssue({ code: 'custom', message: messages.required });
      return;
    }
    if (max === undefined) return;
    for (const language of languages) {
      if (localizedTextOf(value, language).length > max) {
        context.addIssue({ code: 'custom', path: [language], message: messages.tooLong });
      }
    }
  });
}

/**
 * Текст для показа (карточка, реестр): на языке интерфейса, если он есть на рынке и заполнен,
 * иначе на исходном языке, иначе первый заполненный язык рынка.
 */
export function localizedTextDisplay(
  value: LocalizedText | null | undefined,
  uiLanguage: string,
  languages: readonly Language[],
): string {
  const ui = languages.find((language) => language === uiLanguage);
  const order = [ui, value?.source_lang ?? undefined, ...languages];
  for (const language of order) {
    if (!language) continue;
    const text = localizedTextOf(value, language).trim();
    if (text) return text;
  }
  return '';
}
